import { useEffect } from 'react'
import { Button, Loader, Modal } from '@mantine/core'
import { useMutation } from '@tanstack/react-query'
import { useOrpc } from '@/hooks/useClient'
import { describeDoseSpotLaunchFailure } from '@/pages/Patient/components/doseSpotLaunchFailure'
import { DoseSpotLaunchFailurePanel } from './DoseSpotLaunchFailurePanel'

type Props = {
  onClose: () => void
}

/**
 * DoseSpot's own notification summary, embedded — the deep link the bell's pinned row opens.
 * Deep-linking a provider into this page via SSO is a **DoseSpot certification requirement**,
 * not a convenience.
 *
 * Mounted at shell level rather than in the patient chart, because **the bell is global and
 * there is no patient in context**: the counts are per-prescriber and cross-patient, so a
 * refill request in this list could belong to any of their patients. The SSO carries
 * `RefillsErrors=1` and no `PatientId`, which makes the whole pre-flight a token mint — no
 * patient write and no allergy mirror, both being patient-scoped.
 *
 * **Its close handler fires no sync at all**, unlike the Prescribe modal's. Nothing
 * patient-scoped can sync from here, and a refill just resolved could belong to any patient.
 * The badge is corrected by the next pushed count or the ~15-minute reconcile, and any
 * prescription touched reaches the chart under the write-back guarantee — *the chart is
 * correct whenever anyone looks at it*.
 *
 * Mounted only while open, so closing discards the minted URL rather than leaving a consumed
 * single-use code where a reload could replay it. No `message` listener and no overlays over
 * DoseSpot's chrome, for the same two reasons as the Prescribe modal: the embedded app
 * broadcasts an rrweb DOM mirror (PHI included) to the parent window, and the certified app
 * has to render unmodified.
 */
export const DoseSpotNotificationsModal = ({ onClose }: Props) => {
  const orpc = useOrpc()
  const launch = useMutation(orpc.doseSpotLaunchNotifications.mutationOptions())

  // Mint on mount, and only here: this component's lifetime is one launch.
  useEffect(() => {
    launch.mutate({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const failure = launch.isError ? describeDoseSpotLaunchFailure(launch.error) : null

  return (
    <Modal
      opened
      onClose={onClose}
      size="1100px"
      // The stacking scale lives in index.css alongside the scribe rail's.
      zIndex={'var(--z-dosespot-modal)' as unknown as number}
      // A stray click outside must not interrupt a refill the prescriber is part way through
      // approving.
      closeOnClickOutside={false}
      title={
        <div className="flex items-center gap-4">
          <span className="text-base font-semibold">DoseSpot notifications</span>
          <Button size="xs" variant="light" onClick={onClose}>
            Done
          </Button>
        </div>
      }
    >
      {launch.isPending && (
        <div className="flex flex-col items-center gap-3 py-24">
          <Loader size="sm" />
          <div className="text-sm font-medium">Connecting to DoseSpot…</div>
          <div className="text-xs text-gray-500">Usually takes a couple of seconds.</div>
        </div>
      )}

      {/*
       * No `onFix`: the fixable refusals are the patient-scoped ones, and there is no patient
       * here to edit. They are also unreachable — `PATIENT_INCOMPLETE` and `PATIENT_REJECTED`
       * come from steps this launch does not run.
       */}
      {failure && (
        <DoseSpotLaunchFailurePanel
          failure={failure}
          onRetry={() => launch.mutate({})}
          onClose={onClose}
        />
      )}

      {launch.data && (
        <iframe
          title="DoseSpot notifications"
          src={launch.data.url}
          className="w-full"
          style={{ height: 'calc(100vh - 220px)', minHeight: 600, border: 0 }}
        />
      )}
    </Modal>
  )
}
